import React, { createContext, useContext, useState, useRef, useCallback } from 'react';
import { Text, StyleSheet, Animated } from 'react-native';
import { Feather } from '@expo/vector-icons';
import { useColors, ThemeColors } from '../hooks/useColors';

interface ToastContextType {
  showToast: (message: string) => void;
}

const ToastContext = createContext<ToastContextType>({ showToast: () => {} });

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const colors = useColors();
  const styles = createStyles(colors);
  const [message, setMessage] = useState('');
  const animation = useRef(new Animated.Value(0)).current;
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const showToast = useCallback((msg: string) => {
    // Clear any existing timeout to prevent race conditions
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Reset animation if already showing
    animation.setValue(0);
    setMessage(msg);
    
    // Slide down quickly with spring animation
    Animated.spring(animation, {
      toValue: 1,
      useNativeDriver: true,
      tension: 100,
      friction: 8,
    }).start();

    // Auto dismiss after 1.2 seconds
    timeoutRef.current = setTimeout(() => {
      Animated.timing(animation, {
        toValue: 0,
        duration: 200,
        useNativeDriver: true,
      }).start(() => setMessage(''));
    }, 1200);
  }, [animation]);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      {message ? (
        <Animated.View 
          pointerEvents="none"
          style={[
            styles.toast,
            {
              opacity: animation,
              transform: [{
                translateY: animation.interpolate({
                  inputRange: [0, 1],
                  outputRange: [-60, 0],
                }),
              }, {
                scale: animation.interpolate({
                  inputRange: [0, 1],
                  outputRange: [0.9, 1],
                }),
              }],
            },
          ]}
        >
          <Feather name="check-circle" size={18} color="#000" />
          <Text style={styles.text}>{message}</Text>
        </Animated.View>
      ) : null}
    </ToastContext.Provider>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  toast: {
    position: 'absolute',
    top: 60,
    alignSelf: 'center',
    backgroundColor: colors.primary,
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 24,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.2,
    shadowRadius: 8,
    elevation: 6,
  },
  text: {
    color: '#000',
    fontSize: 14,
    fontWeight: '600',
  },
});

export const useToast = () => useContext(ToastContext);
